import Axios from 'axios';
import {
    IServiceByDateRequest,
    IReportServiceByDateResponce,
    IInvoiceByDateRequest,
    IReportInvoiceByDateResponce,
    ICo2ReportResponseEntity
} from '@/domain/IReport';
import config from '../../config.js';
import store from '@/store';
import i18n from '../i18n';

export abstract class ReportApi {
    private static axios = Axios.create({
        baseURL: config.API_URL,
        headers: {
            common: {
                'Content-Type': 'application/json'
            }
        }
    });

    private static encodeQueryParam(value: string | number | null | undefined): string {
        return encodeURIComponent(String(value ?? ''));
    }

    static async getServicesByDate(ServiceByDateRequest: IServiceByDateRequest): Promise<IReportServiceByDateResponce | null> {
        let toplevels = '';
        if (ServiceByDateRequest.toplevel.length > 0) {
            toplevels = ServiceByDateRequest.toplevel.map((a) => a.toplevel_id).toString();
        }
        let url =
            '/servicebydate?servicestartdate=' +
            this.encodeQueryParam(ServiceByDateRequest.service.servicestartdate) +
            '&serviceenddate=' +
            this.encodeQueryParam(ServiceByDateRequest.service.serviceenddate) +
            '&invoicedservicesonly=' +
            this.encodeQueryParam(ServiceByDateRequest.invoicedServices) +
            '&lang=' +
            this.encodeQueryParam(i18n.locale) +
            '&customerid=' +
            this.encodeQueryParam(ServiceByDateRequest.customerid);
        if (ServiceByDateRequest.service.servicedestinationcountry !== '') {
            url = url + '&servicedestinationcountry=' + this.encodeQueryParam(ServiceByDateRequest.service.servicedestinationcountry);
        }
        if (ServiceByDateRequest.passenger.name !== '') {
            url = url + '&passengername=' + this.encodeQueryParam(ServiceByDateRequest.passenger.name);
        }
        if (ServiceByDateRequest.passenger.reference !== '') {
            url = url + '&passengerreference=' + this.encodeQueryParam(ServiceByDateRequest.passenger.reference);
        }
        if (ServiceByDateRequest.booking.employeenumber !== '') {
            url = url + '&bookingemployeenumber=' + this.encodeQueryParam(ServiceByDateRequest.booking.employeenumber);
        }
        if (ServiceByDateRequest.booking.projectcode !== '') {
            url = url + '&bookingprojectcode=' + this.encodeQueryParam(ServiceByDateRequest.booking.projectcode);
        }
        if (ServiceByDateRequest.booking.budgetcode !== '') {
            url = url + '&bookingbudgetcode=' + this.encodeQueryParam(ServiceByDateRequest.booking.budgetcode);
        }
        if (ServiceByDateRequest.ticket.ticketNo !== '') {
            url = url + '&ticketnumber=' + this.encodeQueryParam(ServiceByDateRequest.ticket.ticketNo);
        }
        if (ServiceByDateRequest.booking.customerreference !== '') {
            url = url + '&customerreference=' + this.encodeQueryParam(ServiceByDateRequest.booking.customerreference);
        }
        if (ServiceByDateRequest.booking.finaccount !== '') {
            url = url + '&finaccount=' + this.encodeQueryParam(ServiceByDateRequest.booking.finaccount);
        }
        if (ServiceByDateRequest.booking.menumber !== '') {
            url = url + '&menumber=' + this.encodeQueryParam(ServiceByDateRequest.booking.menumber);
        }
        if (ServiceByDateRequest.booking.bookedbyname !== '') {
            url = url + '&bookername=' + this.encodeQueryParam(ServiceByDateRequest.booking.bookedbyname);
        }
        if (ServiceByDateRequest.saleschannels !== '') {
            url = url + '&saleschannels=' + this.encodeQueryParam(ServiceByDateRequest.saleschannels);
        }
        if (ServiceByDateRequest.datetype !== '') {
            url = url + '&datetype=' + this.encodeQueryParam(ServiceByDateRequest.datetype);
        }
        if (toplevels !== '') {
            url = url + '&toplevels=' + this.encodeQueryParam(toplevels);
        }
        const encodedToken = store.state.token;
        const mydata = { services: null };
        try {
            const response = await this.axios.get<typeof mydata>(url, { headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken } });
            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async getInvoicesByDate(InvoiceByDateRequest: IInvoiceByDateRequest): Promise<IReportInvoiceByDateResponce | null> {
        let url =
            '/invoices?invoicestartdate=' +
            this.encodeQueryParam(InvoiceByDateRequest.invoicestartdate) +
            '&invoiceenddate=' +
            this.encodeQueryParam(InvoiceByDateRequest.invoiceenddate) +
            '&lang=' +
            this.encodeQueryParam(i18n.locale) +
            '&customerid=' +
            this.encodeQueryParam(InvoiceByDateRequest.customerid);
        if (InvoiceByDateRequest.passenger.name !== '') {
            url = url + '&passengername=' + this.encodeQueryParam(InvoiceByDateRequest.passenger.name);
        }
        if (InvoiceByDateRequest.invoicenumber !== '') {
            url = url + '&invoicenumber=' + this.encodeQueryParam(InvoiceByDateRequest.invoicenumber);
        }
        if (InvoiceByDateRequest.paymentstatus !== 'All') {
            url = url + '&paymentstatus=' + this.encodeQueryParam(InvoiceByDateRequest.paymentstatus);
        }
        if (InvoiceByDateRequest.passenger.reference !== '') {
            url = url + '&passengerreference=' + this.encodeQueryParam(InvoiceByDateRequest.passenger.reference);
        }
        if (InvoiceByDateRequest.booking.bookedbyname !== '') {
            url = url + '&bookername=' + this.encodeQueryParam(InvoiceByDateRequest.booking.bookedbyname);
        }
        if (InvoiceByDateRequest.booking.customerreference !== '') {
            url = url + '&customerreference=' + this.encodeQueryParam(InvoiceByDateRequest.booking.customerreference);
        }
        if (InvoiceByDateRequest.booking.projectcode !== '') {
            url = url + '&projectcode=' + this.encodeQueryParam(InvoiceByDateRequest.booking.projectcode);
        }
        if (InvoiceByDateRequest.booking.menumber !== '') {
            url = url + '&menumber=' + this.encodeQueryParam(InvoiceByDateRequest.booking.menumber);
        }
        if (InvoiceByDateRequest.booking.finaccount !== '') {
            url = url + '&finaccount=' + this.encodeQueryParam(InvoiceByDateRequest.booking.finaccount);
        }
        if (InvoiceByDateRequest.booking.budgetcode !== '') {
            url = url + '&budgetcode=' + this.encodeQueryParam(InvoiceByDateRequest.booking.budgetcode);
        }

        const encodedToken = store.state.token;
        const mydata = { invoiceextrafields: false, invoicelist: [] };
        try {
            const response = await this.axios.get<typeof mydata>(url, { headers: { 'Authentication-Estravel': `Bearer ${encodedToken}` } });
            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async getCo2ReportByDate(ServiceByDateRequest: Partial<IServiceByDateRequest>): Promise<ICo2ReportResponseEntity[] | null> {
        const url =
            '/co2_report?date_from=' +
            this.encodeQueryParam(ServiceByDateRequest.service?.servicestartdate) +
            '&date_to=' +
            this.encodeQueryParam(ServiceByDateRequest.service?.serviceenddate) +
            '&customerid=' +
            this.encodeQueryParam(ServiceByDateRequest.customerid);
        const encodedToken = store.state.token;
        try {
            const response = await this.axios({
                method: 'post',
                url,
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }
}
